// Angular
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

// Third Party
import { catchError, map, Observable, of } from 'rxjs';

// Configuration
import { environment } from '@environments/environment';

// Utilities
import { getSessionStorageItem, removeSessionStorageItem, reportConsole, setSessionStorageItem } from '@utils';

// Models
import StandardAuthResponse from 'src/app/billing/shared/models/standard-auth-response-model';

// Types
import GenesysCloudAPIData from 'src/app/billing/shared/static/genesys-cloud-api-data';
import type {
    GCAccessTokenResponse,
    StandardAuthResponseType,
} from 'src/app/billing/shared/types/standard-auth-response.d.ts';

/**
 * Service for handling PKCE authentication flow
 */
@Injectable({
    providedIn: 'root',
})
export class GCAuthenticationService {
    private http = inject(HttpClient);

    private clientIdentifier(auth_uri: string): string {
        let result = '';
        if (auth_uri.includes('dca')) {
            result = environment.app.billing.inin_dca;
        } else {
            // built in client to auth api
            result = environment.app.billing.inin_tca;
        }
        return result;
    }

    private readonly service_constants = {
        code_token: 'gc-session-code-token',
        verifier: 'gc-session-code-verifier',
        challenge: 'gc-session-code-challenge',
        auth_uri: 'gc-session-auth-uri',
        ac_token: 'gc-session-access-token',
    };

    /**
     * Generates a cryptographically secure random string for PKCE code verifier
     * @param length - Length of the string to generate (default: 128)
     * @returns Random URL-safe string
     */
    public generateRandomBase64String(length = 128): string {
        const unreservedCharacters = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-._~';
        const randomString = [...crypto.getRandomValues(new Uint32Array(length))]
            .map((x) => unreservedCharacters[x % unreservedCharacters.length])
            .join('');
        return randomString;
    }

    /**
     * Computes a code challenge from a code verifier using SHA-256
     * @param verifier - The code verifier string
     * @returns Promise resolving to the code challenge
     * @throws Error if verifier length is invalid
     */
    private async computeCodeChallengeFromVerifier(verifier: string): Promise<string> {
        if (verifier.length < 43 || verifier.length > 128) {
            throw new Error(`PKCE Code Verifier (length) must be between 43 and 128 characters`);
        }

        const utf8 = new TextEncoder().encode(verifier);
        const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', utf8);
        const hashBase64 = btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));
        let hashBase64Url = hashBase64.replaceAll('+', '-').replaceAll('/', '_');
        hashBase64Url = hashBase64Url.split('=')[0];
        return hashBase64Url;
    }

    /**
     * Validates if a code verifier matches a code challenge
     * @param codeVerifier - The code verifier to validate
     * @param codeChallenge - The code challenge to validate against
     * @returns Promise resolving to boolean indicating if the code is valid
     */
    public async isCodeVerifiedValid(codeVerifier: string, codeChallenge: string): Promise<boolean> {
        return (await this.computeCodeChallengeFromVerifier(codeVerifier)) === codeChallenge;
    }

    /**
     * Exchanges an authorization code for a token using PKCE flow
     * @param authorizationUri - Base URI of the authorization server
     * @param codeToken - Authorization code received from auth server
     * @param codeVerifier - PKCE code verifier used in the authorization request
     * @returns Observable of StandardAuthResponse containing token response
     */
    public getPKCEToken(codeToken: string): Observable<StandardAuthResponseType> {
        if (!codeToken || (codeToken && codeToken.length === 0)) {
            reportConsole('No code token provided.', 'error');
            return of(StandardAuthResponse);
        }
        setSessionStorageItem(this.service_constants.code_token, codeToken);

        const authorizationUri = getSessionStorageItem(this.service_constants.auth_uri) as string;
        if (!authorizationUri) {
            reportConsole('No authorization URI found for Genesys Cloud authentication', 'error');
            return of(StandardAuthResponse);
        }

        const codeVerifier = getSessionStorageItem(this.service_constants.verifier) as string;
        if (!codeVerifier) {
            reportConsole('No code verifier found for Genesys Cloud authentication', 'error');
            return of(StandardAuthResponse);
        }

        const query = {
            grant_type: 'authorization_code',
            code: codeToken,
            redirect_uri: `${globalThis.location.protocol}//${globalThis.location.host}/billing/oauth/callback`,
            client_id: this.clientIdentifier(authorizationUri),
            code_verifier: codeVerifier,
            code_challenge_method: 'S256',
        };

        const url = `${authorizationUri}/oauth/token`;
        const standardResponse = Object.assign({}, StandardAuthResponse);

        const headers = {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/x-www-form-urlencoded',
        };

        return this.http
            .post(url, null, {
                params: query,
                headers,
                observe: 'response',
            })
            .pipe(
                map((response) => {
                    standardResponse.data = (response.body as GCAccessTokenResponse) || {};
                    standardResponse.status = response.status;
                    standardResponse.url = url;
                    standardResponse.headers = {};

                    response.headers.keys().forEach((key) => {
                        standardResponse.headers[key] = response.headers.get(key);
                    });

                    standardResponse.success = true;
                    return standardResponse;
                }),
                catchError((error) => {
                    reportConsole('Error:', 'error', error);
                    standardResponse.status = error.status;
                    standardResponse.data = error.error;
                    return of(standardResponse);
                }),
            );
    }

    /**
     * Retrieves the PKCE token from session storage
     * @returns PKCE token or null if not found
     */
    public getPKCETokenFromSession(): GCAccessTokenResponse | null {
        return getSessionStorageItem(this.service_constants.ac_token) as GCAccessTokenResponse;
    }

    /**
     * Sets the PKCE token in session storage
     * @param token PKCE token to store
     */
    public setPKCEToken(token_response: GCAccessTokenResponse): void {
        setSessionStorageItem(this.service_constants.ac_token, token_response);
    }

    /**
     * Generates an authorization URL for PKCE OAuth flow and stores verifier in session
     * @param authUrl - Authorization server URL
     * @param clientId - OAuth client identifier
     * @param prompt - Optional login prompt parameter
     * @returns Promise resolving to the generated authorization URL
     */
    public async generateAuthorizationUrl(
        authUrl: string,
        prompt?: string,
    ): Promise<{ success: boolean; url: string }> {
        const response = { success: false, url: '' };

        if (!this.isValidGenesysCloudUrl(authUrl)) {
            reportConsole('Invalid Genesys Cloud authorization URL', 'error');
            return response;
        }

        const codeVerifier = this.generateRandomBase64String();
        const codeChallenge = await this.computeCodeChallengeFromVerifier(codeVerifier);

        // Store PKCE values in session storage
        setSessionStorageItem(this.service_constants.verifier, codeVerifier);
        setSessionStorageItem(this.service_constants.challenge, codeChallenge);

        const query = {
            client_id: this.clientIdentifier(authUrl),
            redirect_uri: `${globalThis.location.protocol}//${globalThis.location.host}/billing/oauth/callback`,
            response_type: 'code',
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
        };

        // Add prompt parameter if provided
        if (prompt) {
            query['prompt'] = prompt;
        }

        const encodedQuery = Object.keys(query)
            .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(query[k])}`)
            .join('&');
        const redirectUrl = `${authUrl}/oauth/authorize?${encodedQuery}`;

        // Validate the final redirect URL
        if (!this.isValidRedirectUrl(redirectUrl)) {
            reportConsole('Invalid redirect URL generated', 'error');
            return response;
        }

        setSessionStorageItem(this.service_constants.auth_uri, authUrl);

        response.url = redirectUrl;
        response.success = true;

        return response;
    }

    /**
     * Clears all GC session storage keys
     */
    public clearSession(): void {
        Object.values(this.service_constants).forEach((key) => removeSessionStorageItem(key));
    }

    /**
     * Generates a logout URL for the given auth URI
     * @param authUrl - Authorization server URL
     * @param redirectUri - URI to redirect to after logout
     */
    public generateLogoutUrl(authUrl: string, redirectUri: string): string {
        const clientId = this.clientIdentifier(authUrl);
        return `${authUrl}/logout?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}`;
    }

    /**
     * Retrieves the stored auth URI from session storage
     */
    public getAuthUriFromSession(): string | null {
        return getSessionStorageItem(this.service_constants.auth_uri) as string | null;
    }

    /**
     * Validates if a URL is a legitimate Genesys Cloud URL
     * @param url - The URL to validate
     * @returns boolean indicating if the URL is valid
     */
    private isValidGenesysCloudUrl(url: string): boolean {
        if (!url) return false;

        try {
            const urlObj = new URL(url);

            // Extract all domains from GenesysCloudAPIData
            const trustedDomains = GenesysCloudAPIData.map((option) => new URL(option.auth).hostname);

            return trustedDomains.includes(urlObj.hostname) && urlObj.protocol === 'https:';
        } catch (_e) {
            return false;
        }
    }

    /**
     * Validates if a redirect URL is safe
     * @param url - The URL to validate
     * @returns boolean indicating if the URL is valid
     */
    private isValidRedirectUrl(url: string): boolean {
        if (!url) return false;

        try {
            const urlObj = new URL(url);

            // Extract all domains from GenesysCloudAPIData
            const trustedDomains = GenesysCloudAPIData.map((option) => new URL(option.auth).hostname);

            // Ensure it's an OAuth authorize endpoint
            const isOAuthPath = urlObj.pathname.includes('/oauth/authorize');

            return trustedDomains.includes(urlObj.hostname) && urlObj.protocol === 'https:' && isOAuthPath;
        } catch (_e) {
            return false;
        }
    }
}
